import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

/**
 * OauthCallbackComponent - MSAL redirect callback handler.
 * 
 * Waits for MsalRedirectComponent (bootstrapped) to finish processing
 * the auth code before checking account state and navigating.
 * This avoids the redirect loop caused by checking isLoggedIn() too early.
 */
@Component({
  standalone: false,
  selector: 'app-oauth-callback',
  template: `<div style="text-align:center;padding:50px;"><h3>Procesando autenticación...</h3></div>`,
  styles: []
})
export class OauthCallbackComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private hasNavigated = false;

  constructor(
    private readonly router: Router,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
  ) {}

  ngOnInit(): void {
    // Wait for MSAL to finish processing the redirect before checking accounts
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (!this.hasNavigated) {
          this.checkAccountAndNavigate();
        }
      });

    // Also listen for explicit login/token success events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(msg => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (!this.hasNavigated) {
          this.checkAccountAndNavigate();
        }
      });
  }

  private checkAccountAndNavigate(): void {
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length > 0) {
      const active = this.msalService.instance.getActiveAccount() || accounts[0];
      this.msalService.instance.setActiveAccount(active);
      console.log('[OauthCallback] Account set, navigating to home.');
      this.hasNavigated = true;
      this.router.navigate(['/']);
    } else {
      // No accounts yet — MSAL will trigger loginRedirect via MsalGuard/app.component
      console.log('[OauthCallback] No accounts found after redirect');
      this.hasNavigated = true;
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}

